import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import uuidv4 from 'src/utils/uuidv4'
import { cloneDeep } from 'lodash'
import { basicPaths } from 'src/routes/basic-paths'
import { ajaxCall } from './axios'
// eslint-disable-next-line import/no-cycle
import { updateErrors } from './form'
import { showMessage } from './message'

const initialState = {
  appConfig: window.appConfig || {},
  titleBlock: {
    title: null,
    breadcrumbs: [],
  },
  pageType: null,
  nodeList: {},
  nodeListSub: {},
  pageContent: {},
  sidebar: [],
  table: [],
  pagination: null,
  getFilteredItems: false,
  filteredItems: null,
  tableFilters: {},
  fieldsModified: [],
  clearForm: false,
}

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateTitleBlock: (state, action) => {
      state.titleBlock = action.payload
    },
    updateSidebar: (state, action) => {
      state.sidebar = action.payload
    },
    updateNodeList: (state, action) => {
      const{nodeList} = action.payload
      state.nodeList = nodeList
    },
    updateNodeListSub: (state, action) => {
      const{nodeListSub} = action.payload
      state.nodeListSub = nodeListSub
    },
    updatePage: (state, action) => {
      state.pageContent = action.payload
    },
    updateTableDataPageContent: (state, action) => {
      state.pageContent.tableData = action.payload
    },
    updatePageType: (state, action) => {
      state.pageType = action.payload
    },
    updateTable: (state, action) => {
      state.table = action.payload
    },
    getFilteredItems: (state) => {
      state.getFilteredItems = true

      state.filteredItems = null
    },
    setFilteredItems: (state, action) => {
      state.getFilteredItems = false

      state.filteredItems = action.payload
    },
    resetFilteredItems: (state) => {
      state.filteredItems = null
    },
    updatePagination: (state, action) => {
      state.pagination = action.payload
    },
    updateTableFilters: (state, action) => {
      state.tableFilters = action.payload
    },
    updateResource: (state, action) => {
      state.pageContent.resources = state.pageContent.resources.map((res) => {
        if (res.id === action.payload.id) {
          res.data = action.payload.data
        }

        return res
      })
    },
    updateFields: (state, action) => {
      const { field, index, isTop, name } = action.payload

      const actionsType = isTop ? 'topActions' : 'bottomActions'

      state.pageContent[actionsType] = state.pageContent[actionsType].map((item, i) => {
        if (i === index) {
          item.fields = (item.fields || []).map((el) => {
            if (el.name === name) {
              return field
            }

            return el
          })
        }

        return item
      })
    },
    updateTableRow: (state, action) => {
      const { id, idHidden, toUpdate } = action.payload

      state.pageContent.tableData = state.pageContent.tableData.map((row) => {
        if (row.id === id) {
          Object.keys(toUpdate).forEach((key) => {
            if (typeof idHidden === 'undefined') {
              row[key] = toUpdate[key]
            } else if (key.startsWith('additional-infos')) {
              const splitTableId = key.split('_')

              const idInfos = splitTableId[1]

              const additionalInfosArray = row.hiddenItems[idHidden].additionalInfos

              if (additionalInfosArray) {
                const indexInfosItem = additionalInfosArray.findIndex(
                  (element) => element.id === idInfos
                )

                additionalInfosArray[indexInfosItem].value = toUpdate[key]
              }
            } else {
              row.hiddenItems[idHidden][key] = toUpdate[key]
            }
          })
        }

        return row
      })
    },
    updateAddedRows: (state, action) => {
    },

    addStruNode: (state, action) => {
      const { parent, type, labels } = action.payload

      const label = state.pageContent.labelField

      if (parent) {
        const nodes = cloneDeep(state.pageContent.nodeList)

        const findAndAdd = (arr) => {
          arr.forEach((item) => {
            if (item.id === parent) {
              if (type === 'node') {
                if (!item.children) {
                  item.children = []
                }

                return item.children.push({
                  type,
                  [label]: labels[`${type}Default`],
                  id: uuidv4(),
                })
              }

              if (!item.contents) {
                item.contents = []
              }

              return item.contents.push({
                type,
                [label]: labels[`${type}Default`],
                id: uuidv4(),
              })
            }

            if (item.children) {
              findAndAdd(item.children)
            }
          })
        }

        findAndAdd(nodes)

        state.pageContent.nodeList = nodes
      } else {
        state.pageContent.nodeList = [
          ...state.pageContent.nodeList,
          {
            type: 'node',
            [label]: labels.nodeDefault,
            id: uuidv4(),
          },
        ]
      }
    },
    deleteStruNode: (state, action) => {
      const { id } = action.payload

      const nodes = cloneDeep(state.pageContent.nodeList)

      const findAndDelete = (arr) => {
        arr.forEach((item) => {
          if (item.id === id) {
            const index = arr.indexOf(item)

            return arr.splice(index, 1)
          }

          if (item.children) {
            findAndDelete(item.children)
          }

          if (item.contents) {
            findAndDelete(item.contents)
          }
        })
      }

      findAndDelete(nodes)

      state.pageContent.nodeList = nodes
    },
    changeStruProp: (state, action) => {
      const { id, toUpdate } = action.payload || {}

      const nodes = cloneDeep(state.pageContent.nodeList)

      const findAndChange = (arr) => {
        arr.forEach((item) => {
          if (item.id === id) {
            Object.entries(toUpdate).forEach(([key, val]) => {
              item[key] = val
            })
          }

          if (item.children) {
            findAndChange(item.children)
          }

          if (item.contents) {
            findAndChange(item.contents)
          }
        })
      }

      findAndChange(nodes)

      state.pageContent.nodeList = nodes
    },
    updateProps: (state, action) => {
      const { name, toUpdate } = action.payload

      state.pageContent.fields = state.pageContent.fields.map((field) => {
        if (field.name === name) {
          return {
            ...field,
            ...toUpdate,
          }
        }

        return field
      })
    },
    toggleTableUsage: (state, action) => {
      const { rowId, usageId } = action.payload

      const newTables = [...state.pageContent.tables]

      newTables.forEach((table) => {
        table.data.forEach((elem) => {
          if (elem.id === rowId) {
            const found = elem.usage.options.find((op) => op.value === usageId)

            found.checked = !found.checked
          }
        })
      })

      state.pageContent.tables = newTables
    },
    selectAllUsage: (state, action) => {
      const { label, deselect } = action.payload

      const newTables = [...state.pageContent.tables]

      newTables.forEach((table) => {
        table.data.forEach((elem) => {
          if (elem.usage && elem.usage.options) {
            elem.usage.options.forEach((op) => {
              if (op.label === label) {
                op.checked = !deselect
              }
            })
          }
        })
      })

      state.pageContent.tables = newTables
    },
    addGenNode: (state, action) => {
      const { parent, type, labels } = action.payload

      const label = state.pageContent.labelField

      if (parent) {
        const nodes = cloneDeep(state.nodeList)

        const findAndAdd = (arr) => {
          arr.forEach((item) => {
            if (item.id === parent) {
              if (type === 'node') {
                if (!item.children) {
                  item.children = []
                }

                return item.children.push({
                  type,
                  [label]: labels[`${type}Default`],
                  id: uuidv4(),
                })
              }
            }

            if (item.children) {
              findAndAdd(item.children)
            }
          })
        }

        findAndAdd(nodes)

        state.nodeList = nodes
      } else {
        state.nodeList = [
          ...state.nodeList,
          {
            type: 'node',
            [label]: labels.nodeDefault,
            id: uuidv4(),
          },
        ]
      }
    },
    deleteGenNode: (state, action) => {
      const { id } = action.payload

      const nodes = cloneDeep(state.pageContent.nodeList)

      const findAndDelete = (arr) => {
        arr.forEach((item) => {
          if (item.id === id) {
            const index = arr.indexOf(item)

            return arr.splice(index, 1)
          }

          if (item.children) {
            findAndDelete(item.children)
          }

          if (item.contents) {
            findAndDelete(item.contents)
          }
        })
      }

      findAndDelete(nodes)

      state.pageContent.nodeList = nodes
    },
    addRemoveFields: (state, action) => {
      state.fieldsModified = removeCycles(action.payload.fields) // Removes circular references
    },
    modifyClear: (state, action) => {
      state.clearForm = action.payload
    },
  },
})

export const {
  updateTitleBlock,
  updateSidebar,
  updatePage,
  updateTable,
  getFilteredItems,
  updateNodeList,
  updateNodeListSub,
  resetFilteredItems,
  setFilteredItems,
  updatePagination,
  updatePageType,
  updateResource,
  updateTableFilters,
  updateFields,
  updateTableRow,
  addStruNode,
  changeStruProp,
  updateProps,
  deleteStruNode,
  toggleTableUsage,
  selectAllUsage,
  addRemoveFields,
  modifyClear,
  updateTableDataPageContent,
} = appSlice.actions

export const getPage = createAsyncThunk('app/page', async (path, thunkAPI) => {
  thunkAPI.dispatch(updatePage({}))

  thunkAPI.dispatch(updateTable([]))

  thunkAPI.dispatch(updateErrors({}))

  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: path,
        method: 'get',
      },
      success: (res) => {
        thunkAPI.dispatch(updateTitleBlock(res.data.titleBlock))

        thunkAPI.dispatch(updatePage(res.data.pageContent))

        thunkAPI.dispatch(updatePageType(res.data.pageType))
      },
      error: () => {},
      activeLoading: true,
    })
  )
})

export const getTableDataPageContent = createAsyncThunk('app/page', async (path, thunkAPI) => {
  if (!path.startsWith("/metadata-manager")){
    path = `/metadata-manager${path}/page`
  }

  path = `${path}/page`

  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: path,
        method: 'get',
      },
      success: (res) => {
        thunkAPI.dispatch(updateTableDataPageContent(res.data.pageContent.tableData))
      },
      error: () => {},
      activeLoading: false,
    })
  )
})

export const getTableDataPaginated = createAsyncThunk('app/page', async ({ path, pagination }, thunkAPI) => {
  console.log("Path iniziale: ", path)
  path = path.slice(1)
  console.log("Path dopo slice: ", path)


  path = `/${path}/data`
  console.log("Path dopo match: ", path)

  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: path,
        method: 'get',
        params: {
          page: pagination
        },
      },
      success: (res) => {
        console.log("Risposta: ", res)
        thunkAPI.dispatch(updateTable(res.data?.data.results))

        thunkAPI.dispatch(updatePagination(res.data?.data.pagination))

      },
      error: () => {
      },
      activeLoading: false,
    })
  )
})

export const loadTable = createAsyncThunk('app/table', async (path, thunkAPI) => {
  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: path,
        method: 'get',
      },
      success: (res) => {
        thunkAPI.dispatch(updateTable(res.data?.data.results))

        thunkAPI.dispatch(updatePagination(res.data?.data.pagination))
      },
      error: () => {},
      activeLoading: false,
    })
  )
})

export const loadTableAjax = createAsyncThunk('app/table-ajax', async (obj, thunkAPI) => {
  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: obj.url,
        method: 'get',
        params: obj.params,
      },
      success: (res) => {
        thunkAPI.dispatch(updateTable(res.data?.data.results))

        thunkAPI.dispatch(updatePagination(res.data?.data.pagination))
      },
      error: () => {},
      activeLoading: false,
    })
  )
})

export const getSidebar = createAsyncThunk('app/user-manager/sidebar', async (_, thunkAPI) => {
  const { appConfig } = thunkAPI.getState().app

  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: appConfig.actions.sidebar.path,
        method: 'get',
      },
      success: (res) => {
        thunkAPI.dispatch(updateSidebar(res.data))
      },
      error: () => {},
      activeLoading: false,
    })
  )
})

export const reloadPageContent = createAsyncThunk('app/page', async (path, thunkAPI) => {
  thunkAPI.dispatch(updatePage({}))

  thunkAPI.dispatch(updateErrors({}))

  thunkAPI.dispatch(
    ajaxCall({
      config: {
        url: path,
        method: 'get',
      },
      success: (res) => {
        thunkAPI.dispatch(updatePage(res.data.pageContent))

        thunkAPI.dispatch(updatePageType(res.data.pageType))
      },
      error: () => {},
      activeLoading: false,
    })
  )
})

export default appSlice.reducer

const removeCycles = (obj, seen = new WeakSet()) => {
  if (typeof obj !== "object" || obj === null) return obj
  if (seen.has(obj)) return [] // Replace with a placeholder to avoid cycles

  seen.add(obj)
  if (Array.isArray(obj)) {
    return obj.map(item => removeCycles(item, seen))
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, removeCycles(value, seen)])
  )
}
